import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarToday as CalendarIcon,
  Storage as StorageIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as TaxIcon,
  Savings as SavingsIcon,
  SwapHoriz as TransactionIcon,
  TableChart as DatabaseIcon,
  CompareArrows as CompareIcon,
  Webhook as WebhookIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  Security as SecurityIcon,
  Contacts as ContactsIcon,
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';

import { FIT_TABLE_SX, FIT_TABLE_CONTAINER_SX, FitColgroup, fitTruncateSx } from '../utils/fitTable';
interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  family_id?: number | null;
  family_name?: string | null;
}

interface Page {
  path: string;
  label: string;
  icon: React.ReactNode;
  category: string;
}

interface PagePermission {
  page: Page;
  user_permissions: {
    [userId: number]: {
      user: User;
      can_access: boolean;
      has_permission: boolean;
    };
  };
}

const PAGES: Page[] = [
  { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon />, category: 'Main' },
  { path: '/balances', label: 'Balance Management', icon: <AccountBalanceIcon />, category: 'Financial' },
  { path: '/balances-manual', label: 'Balances Manual', icon: <AccountBalanceIcon />, category: 'Financial' },
  { path: '/revolut', label: 'Revolut', icon: <TransactionIcon />, category: 'Financial' },
  { path: '/pandle-transactions', label: 'Pandle Transactions', icon: <DescriptionIcon />, category: 'Financial' },
  { path: '/personal-finances', label: 'Personal Finances', icon: <SavingsIcon />, category: 'Financial' },
  { path: '/receipts-invoices', label: 'Receipts & Invoices', icon: <AttachFileIcon />, category: 'Financial' },
  { path: '/bookings', label: 'Bookings', icon: <CalendarIcon />, category: 'Financial' },
  { path: '/hosting', label: 'Hosting', icon: <ReceiptIcon />, category: 'Financial' },
  { path: '/loans-schedule', label: 'Loan Amortization', icon: <TrendingUpIcon />, category: 'Financial' },
  { path: '/tax', label: 'Tax Calculations', icon: <TaxIcon />, category: 'Financial' },
  { path: '/vat', label: 'VAT', icon: <TaxIcon />, category: 'Financial' },
  { path: '/annual-returns', label: 'Annual Returns', icon: <TaxIcon />, category: 'Financial' },
  { path: '/performance', label: 'Performance', icon: <TrendingUpIcon />, category: 'Financial' },
  { path: '/transaction-classification-review', label: 'Classification Review', icon: <TaxIcon />, category: 'Financial' },
  { path: '/gl-transactions', label: 'General Ledger', icon: <AccountBalanceIcon />, category: 'Financial' },
  { path: '/database-overview', label: 'Database', icon: <DatabaseIcon />, category: 'Financial' },
  { path: '/rbac', label: 'RBAC Management', icon: <SecurityIcon />, category: 'Administration' },
  { path: '/entities', label: 'Entities', icon: <StorageIcon />, category: 'Administration' },
  { path: '/transaction-comparison', label: 'Transaction Comparison', icon: <CompareIcon />, category: 'Administration' },
];

export const PagesPermissions: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [pagePermissions, setPagePermissions] = useState<Record<string, PagePermission>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPagePermissions();
  }, []);

  const fetchPagePermissions = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch page permissions from API
      const response = await api.get('/api/admin/page-permissions');
      const fetchedUsers: User[] = response.data.users || [];
      const fetchedPages: PagePermission[] = response.data.pages || [];
      
      // Ensure users have family_id and family_name (handle null/undefined from API)
      const usersWithFamily = fetchedUsers.map(user => ({
        ...user,
        family_id: user.family_id ?? null,
        family_name: user.family_name ?? null
      }));
      
      setUsers(usersWithFamily);

      // Convert API response to local state format
      const permissions: Record<string, PagePermission> = {};
      fetchedPages.forEach((pagePerm: any) => {
        const pagePath = pagePerm.page.path;
        permissions[pagePath] = {
          page: {
            path: pagePath,
            label: pagePerm.page.label,
            icon: PAGES.find(p => p.path === pagePath)?.icon || <DashboardIcon />,
            category: pagePerm.page.category
          },
          user_permissions: pagePerm.user_permissions || {}
        };
      });

      setPagePermissions(permissions);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch page permissions');
      console.error('Error fetching page permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (
    pagePath: string,
    userId: number,
    value: boolean
  ) => {
    try {
      setError('');
      
      // Call backend API to update permission
      await api.put(`/api/admin/page-permissions${pagePath}/user/${userId}`, {
        can_access: value
      });

      // Update local state
      setPagePermissions(prev => {
        const updated = { ...prev };
        if (updated[pagePath]) {
          updated[pagePath] = {
            ...updated[pagePath],
            user_permissions: {
              ...updated[pagePath].user_permissions,
              [userId]: {
                ...updated[pagePath].user_permissions[userId],
                can_access: value,
                has_permission: value
              }
            }
          };
        }
        return updated;
      });

      setSuccess('Permission updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update permission');
      console.error('Error updating permission:', err);
    }
  };

  // Group users by family (prioritize family grouping over role)
  const groupUsersByFamily = (users: User[]) => {
    const grouped: { [familyKey: string]: User[] } = {
      'no-family': []
    };
    
    // Separate admin users (non-owner, no family) into its own group
    const adminUsers: User[] = [];
    
    users.forEach(user => {
      // Check if user has a valid family_id (not null, not undefined, not 0)
      // This includes owners like Omar who should be grouped with their family
      if (user.family_id != null && user.family_id !== undefined && user.family_id !== 0) {
        // Group by family (includes owner users like Omar with their family)
        const familyKey = `family-${user.family_id}`;
        if (!grouped[familyKey]) {
          grouped[familyKey] = [];
        }
        grouped[familyKey].push(user);
      } else if (user.role === 'admin') {
        // Admin users without a family go to separate admin group
        adminUsers.push(user);
      } else {
        // Users without family (non-admin, non-owner)
        grouped['no-family'].push(user);
      }
    });
    
    // Add admin users as a separate group if any exist
    if (adminUsers.length > 0) {
      grouped['admin'] = adminUsers;
    }
    
    return grouped;
  };

  const userGroups = useMemo(() => groupUsersByFamily(users), [users]);

  const userColumnKeys = useMemo(
    () =>
      Object.entries(userGroups).flatMap(([familyKey, familyUsers]) =>
        familyUsers.map(user => `${familyKey}-${user.id}`)
      ),
    [userGroups]
  );

  // Group pages by category
  const pagesByCategory = useMemo(() => {
    const grouped: { [category: string]: Page[] } = {};
    PAGES.forEach(page => {
      if (!grouped[page.category]) {
        grouped[page.category] = [];
      }
      grouped[page.category].push(page);
    });
    return grouped;
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {Object.entries(pagesByCategory).map(([category, pages]) => (
        <Card key={category} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              {category}
            </Typography>
            
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '70vh', overflowY: 'auto' }}
            >
              <Table size="small" stickyHeader sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'page', pct: 20 },
                    ...userColumnKeys.map(key => ({
                      key,
                      pct: Number((80 / Math.max(1, userColumnKeys.length)).toFixed(3)),
                    })),
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell
                      className="fit-table-no-clip"
                      sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10 }}
                    >
                      <strong>Page</strong>
                    </TableCell>
                    {/* User columns grouped by family */}
                    {Object.entries(userGroups).map(([familyKey, familyUsers]) => {
                      if (familyUsers.length === 0) return null;
                      
                      const familyName = familyKey === 'admin' 
                        ? 'Admin' 
                        : familyKey === 'no-family'
                        ? 'No Family'
                        : familyUsers[0].family_name || `Family ${familyUsers[0].family_id}`;
                      
                      return (
                        <React.Fragment key={familyKey}>
                          <TableCell 
                            colSpan={familyUsers.length} 
                            align="center" 
                            sx={{ 
                              backgroundColor: familyKey === 'admin' ? '#f5f5f5' : '#e3f2fd',
                              fontWeight: 'bold',
                              borderLeft: '1px solid #e0e0e0'
                            }}
                          >
                            <Box component="span" sx={fitTruncateSx} title={familyName}>
                              {familyName}
                            </Box>
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10 }}>
                      {/* Empty cell for page name column */}
                    </TableCell>
                    {Object.entries(userGroups).map(([familyKey, familyUsers]) => (
                      familyUsers.map(user => (
                        <TableCell 
                          key={`${familyKey}-${user.id}`} 
                          align="center"
                          sx={{ 
                            
                            backgroundColor: familyKey === 'admin' ? '#fafafa' : '#f5f9ff',
                            borderLeft: '1px solid #e0e0e0'
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              display="block"
                              fontWeight="bold"
                              sx={fitTruncateSx}
                              title={`${user.first_name} ${user.last_name}`}
                            >
                              {user.first_name} {user.last_name}
                            </Typography>
                            {(user.role === 'admin' || user.role === 'owner') && (
                              <Chip 
                                label={user.role === 'owner' ? 'OWNER' : 'ADMIN'} 
                                size="small" 
                                color={user.role === 'owner' ? 'error' : 'default'}
                                sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                            {user.role !== 'admin' && user.role !== 'owner' && (
                              <Chip 
                                label={user.role.toUpperCase()} 
                                size="small" 
                                sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      ))
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pages.map((page) => {
                    const pagePerm = pagePermissions[page.path];
                    if (!pagePerm) return null;

                    return (
                      <TableRow key={page.path}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            {page.icon}
                            <Typography variant="body2" sx={fitTruncateSx} title={page.label}>
                              {page.label}
                            </Typography>
                          </Box>
                        </TableCell>
                        {Object.entries(userGroups).map(([userFamilyKey, familyUsers]) => (
                          familyUsers.map(user => {
                            const perm = pagePerm.user_permissions[user.id] || {
                              can_access: false,
                              has_permission: false
                            };
                            
                            // Owner always have access (read-only in UI), Admin users can be managed
                            const isOwner = user.role === 'owner';
                            
                            return (
                              <TableCell 
                                key={`${page.path}-${user.id}`} 
                                align="center"
                                sx={{ borderLeft: '1px solid #e0e0e0' }}
                              >
                                <Switch
                                  size="small"
                                  checked={isOwner ? true : perm.can_access}
                                  disabled={isOwner}
                                  onChange={(e) => {
                                    if (!isOwner) {
                                      handlePermissionChange(
                                        page.path,
                                        user.id,
                                        e.target.checked
                                      );
                                    }
                                  }}
                                />
                              </TableCell>
                            );
                          })
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};



